/**
 * AllItemsView - Consolidated view of all QA items
 * Shows requirements, test scenarios, and test cases in a filterable list
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import {
  Search, FileText, TestTube, Layers, Filter,
  Loader2, ChevronRight, RefreshCw, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const getBackendURL = () => process.env.REACT_APP_BACKEND_URL || '';

// Item type config
const itemTypeConfig = {
  requirement: { 
    icon: FileText, 
    label: 'Requirement', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    iconColor: 'text-blue-600'
  },
  scenario: { 
    icon: Layers, 
    label: 'Scenario', 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    iconColor: 'text-purple-600'
  },
  test_case: { 
    icon: TestTube, 
    label: 'Test Case', 
    color: 'bg-green-100 text-green-700 border-green-200',
    iconColor: 'text-green-600'
  }
};

// Status config
const statusConfig = {
  draft: { color: 'bg-gray-100 text-gray-600', label: 'Draft' },
  ready: { color: 'bg-blue-100 text-blue-700', label: 'Ready' },
  in_progress: { color: 'bg-amber-100 text-amber-700', label: 'In Progress' },
  done: { color: 'bg-green-100 text-green-700', label: 'Done' },
  passed: { color: 'bg-green-100 text-green-700', label: 'Passed' },
  failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
  blocked: { color: 'bg-purple-100 text-purple-700', label: 'Blocked' },
  skipped: { color: 'bg-slate-100 text-slate-600', label: 'Skipped' }
};

// Priority config
const priorityConfig = {
  low: { color: 'bg-slate-100 text-slate-600', label: 'Low' },
  medium: { color: 'bg-blue-100 text-blue-700', label: 'Medium' },
  high: { color: 'bg-orange-100 text-orange-700', label: 'High' },
  critical: { color: 'bg-red-100 text-red-700', label: 'Critical' }
};

export default function AllItemsView({ 
  projectId,
  projectSlug,
  onNavigateToRequirement,
  onNavigateToScenario,
  onNavigateToTestCase
}) {
  const BACKEND_URL = getBackendURL();
  
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({});
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Fetch items
  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { project_id: projectId };
      
      if (typeFilter && typeFilter !== 'all') {
        params.item_type = typeFilter;
      }
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/all-items`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
      setTypeCounts(response.data.type_counts || {});
    } catch (err) {
      console.error('Failed to fetch all items:', err);
      toast({
        title: 'Error',
        description: 'Failed to load items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, typeFilter, statusFilter, debouncedSearch, BACKEND_URL]);
  
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  
  // Handle item click
  const handleItemClick = (item) => {
    switch (item.item_type) {
      case 'requirement':
        onNavigateToRequirement?.(item.key);
        break;
      case 'scenario':
        onNavigateToScenario?.(item.key);
        break;
      case 'test_case':
        onNavigateToTestCase?.(item.key);
        break;
      default:
        break;
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setStatusFilter('all');
  };
  
  const hasActiveFilters = searchQuery || typeFilter !== 'all' || statusFilter !== 'all';
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="h-full flex flex-col" data-testid="all-items-view">
      {/* Header with filters */}
      <div className="p-4 border-b bg-white space-y-3">
        {/* Summary counts */}
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">
            <strong>{total}</strong> items total
          </span>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1">
              <FileText className="w-3 h-3" />
              {typeCounts.requirement || 0} Requirements
            </Badge>
            <Badge variant="outline" className="gap-1">
              <Layers className="w-3 h-3" />
              {typeCounts.scenario || 0} Scenarios
            </Badge>
            <Badge variant="outline" className="gap-1">
              <TestTube className="w-3 h-3" />
              {typeCounts.test_case || 0} Test Cases
            </Badge>
          </div>
        </div>
        
        {/* Filter controls */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="all-items-search"
              placeholder="Search by key or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          {/* Type filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="all-items-type-filter">
              <Filter className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="requirement">Requirements</SelectItem>
              <SelectItem value="scenario">Scenarios</SelectItem>
              <SelectItem value="test_case">Test Cases</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Status filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="all-items-status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="passed">Passed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-gray-500"
              data-testid="all-items-clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
          
          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchItems}
            disabled={loading}
            data-testid="all-items-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* Items list */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-500">
            <Search className="w-10 h-10 mb-2 text-gray-300" />
            <p>No items found</p>
            {hasActiveFilters && (
              <Button
                variant="link"
                size="sm"
                onClick={clearFilters}
                className="mt-2"
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => {
              const typeConf = itemTypeConfig[item.item_type] || itemTypeConfig.requirement;
              const TypeIcon = typeConf.icon;
              const statusConf = statusConfig[item.status] || statusConfig.draft;
              const priorityConf = priorityConfig[item.priority];
              
              return (
                <div
                  key={`${item.item_type}-${item.id}`}
                  data-testid={`all-items-row-${item.key}`}
                  onClick={() => handleItemClick(item)}
                  className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:border-indigo-300 hover:shadow-sm cursor-pointer transition-all group"
                >
                  {/* Type icon */}
                  <div className={`p-2 rounded-lg ${typeConf.color}`}>
                    <TypeIcon className={`w-4 h-4 ${typeConf.iconColor}`} />
                  </div>
                  
                  {/* Key */}
                  <span className="font-mono text-sm font-medium text-indigo-600 min-w-[70px]">
                    {item.key}
                  </span>
                  
                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.title}
                    </p>
                    {item.parent_key && (
                      <p className="text-xs text-gray-500">
                        Parent: {item.parent_key}
                      </p>
                    )}
                  </div>
                  
                  {/* Type badge */}
                  <Badge variant="outline" className={`${typeConf.color} text-xs`}>
                    {typeConf.label}
                  </Badge>
                  
                  {/* Status badge */}
                  <Badge className={`${statusConf.color} text-xs`}>
                    {statusConf.label}
                  </Badge>
                  
                  {/* Priority badge */}
                  {priorityConf && (
                    <Badge className={`${priorityConf.color} text-xs`}>
                      {priorityConf.label}
                    </Badge>
                  )}
                  
                  {/* Assignee */}
                  {item.assignee_name && (
                    <span className="text-xs text-gray-500 truncate max-w-[100px]" title={item.assignee_name}>
                      {item.assignee_name}
                    </span>
                  )}
                  
                  {/* Updated date */}
                  <span className="text-xs text-gray-400 min-w-[50px] text-right">
                    {formatDate(item.updated_at || item.created_at)}
                  </span>
                  
                  {/* Arrow */}
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 transition-colors" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
